/**
 * Offline Storage Service
 * IndexedDB wrapper for offline data persistence
 * Handles visits, orders, expenses with sync queue
 */

const DB_NAME = 'pharmatrack_offline';
const DB_VERSION = 1;

// Store names
const STORES = {
    PENDING_VISITS: 'pending_visits',
    PENDING_ORDERS: 'pending_orders',
    PENDING_EXPENSES: 'pending_expenses',
    SYNC_QUEUE: 'sync_queue',
    CACHE: 'cache'
};

let db = null;

/**
 * Initialize IndexedDB
 */
export const initOfflineStorage = () => {
    return new Promise((resolve, reject) => {
        if (db) {
            resolve(db);
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('[OfflineStorage] Failed to open database:', request.error);
            reject(request.error);
        };

        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const database = event.target.result;

            // Create stores if they don't exist
            if (!database.objectStoreNames.contains(STORES.PENDING_VISITS)) {
                const visitStore = database.createObjectStore(STORES.PENDING_VISITS, { keyPath: 'id' });
                visitStore.createIndex('created_at', 'created_at', { unique: false });
            }

            if (!database.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
                const orderStore = database.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'id' });
                orderStore.createIndex('created_at', 'created_at', { unique: false });
            }

            if (!database.objectStoreNames.contains(STORES.PENDING_EXPENSES)) {
                const expenseStore = database.createObjectStore(STORES.PENDING_EXPENSES, { keyPath: 'id' });
                expenseStore.createIndex('created_at', 'created_at', { unique: false });
            }

            if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
                const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
                syncStore.createIndex('type', 'type', { unique: false });
                syncStore.createIndex('status', 'status', { unique: false });
            }

            if (!database.objectStoreNames.contains(STORES.CACHE)) {
                database.createObjectStore(STORES.CACHE, { keyPath: 'key' });
            }

        };
    });
};

/**
 * Generic store operations
 */
const getStore = async (storeName, mode = 'readonly') => {
    if (!db) await initOfflineStorage();
    const transaction = db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
};

/**
 * Add item to store
 */
export const addToStore = async (storeName, item) => {
    const store = await getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
        const request = store.add(item);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Get item from store
 */
export const getFromStore = async (storeName, key) => {
    const store = await getStore(storeName);
    return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Get all items from store
 */
export const getAllFromStore = async (storeName) => {
    const store = await getStore(storeName);
    return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
};

/**
 * Delete item from store
 */
export const deleteFromStore = async (storeName, key) => {
    const store = await getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

/**
 * Clear store
 */
export const clearStore = async (storeName) => {
    const store = await getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

/**
 * Count items in store
 */
export const countInStore = async (storeName) => {
    const store = await getStore(storeName);
    return new Promise((resolve, reject) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

// ============== Specific Operations ==============

/**
 * Save visit offline
 */
export const saveVisitOffline = async (visitData) => {
    const visit = {
        ...visitData,
        id: `offline_visit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString(),
        offline: true,
        sync_status: 'pending'
    };

    await addToStore(STORES.PENDING_VISITS, visit);
    await addToSyncQueue('visit', visit.id, visitData);

    return visit;
};

/**
 * Save order offline
 */
export const saveOrderOffline = async (orderData) => {
    const order = {
        ...orderData,
        id: `offline_order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString(),
        offline: true,
        sync_status: 'pending'
    };

    await addToStore(STORES.PENDING_ORDERS, order);
    await addToSyncQueue('order', order.id, orderData);

    return order;
};

/**
 * Save expense offline
 */
export const saveExpenseOffline = async (expenseData) => {
    const expense = {
        ...expenseData,
        id: `offline_expense_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString(),
        offline: true,
        sync_status: 'pending'
    };

    await addToStore(STORES.PENDING_EXPENSES, expense);
    await addToSyncQueue('expense', expense.id, expenseData);

    return expense;
};

/**
 * Add item to sync queue
 */
export const addToSyncQueue = async (type, localId, data) => {
    const queueItem = {
        id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type, // 'visit', 'order', 'expense'
        local_id: localId,
        data,
        status: 'pending', // 'pending', 'syncing', 'failed', 'synced'
        retries: 0,
        created_at: new Date().toISOString(),
        last_attempt: null,
        error: null
    };

    await addToStore(STORES.SYNC_QUEUE, queueItem);
    return queueItem;
};

/**
 * Get pending sync items
 */
export const getPendingSyncItems = async () => {
    const items = await getAllFromStore(STORES.SYNC_QUEUE);
    return items.filter(item => item.status === 'pending' || item.status === 'failed');
};

/**
 * Update sync item status
 */
export const updateSyncItemStatus = async (id, status, error = null) => {
    const store = await getStore(STORES.SYNC_QUEUE, 'readwrite');
    return new Promise((resolve, reject) => {
        const getRequest = store.get(id);
        getRequest.onsuccess = () => {
            const item = getRequest.result;
            if (item) {
                item.status = status;
                item.last_attempt = new Date().toISOString();
                if (error) item.error = error;
                if (status === 'failed') item.retries = (item.retries || 0) + 1;

                const putRequest = store.put(item);
                putRequest.onsuccess = () => resolve(item);
                putRequest.onerror = () => reject(putRequest.error);
            } else {
                resolve(null);
            }
        };
        getRequest.onerror = () => reject(getRequest.error);
    });
};

/**
 * Get offline stats
 */
export const getOfflineStats = async () => {
    const [visits, orders, expenses, queue] = await Promise.all([
        countInStore(STORES.PENDING_VISITS),
        countInStore(STORES.PENDING_ORDERS),
        countInStore(STORES.PENDING_EXPENSES),
        getPendingSyncItems()
    ]);

    return {
        pendingVisits: visits,
        pendingOrders: orders,
        pendingExpenses: expenses,
        pendingSync: queue.length,
        totalPending: visits + orders + expenses
    };
};

/**
 * Clear synced item after successful sync
 */
export const clearSyncedItem = async (type, localId) => {
    // Remove from type-specific store
    const storeMap = {
        visit: STORES.PENDING_VISITS,
        order: STORES.PENDING_ORDERS,
        expense: STORES.PENDING_EXPENSES
    };

    const storeName = storeMap[type];
    if (storeName) {
        await deleteFromStore(storeName, localId);
    }

    // Remove from sync queue
    const queue = await getAllFromStore(STORES.SYNC_QUEUE);
    const item = queue.find(q => q.local_id === localId);
    if (item) {
        await deleteFromStore(STORES.SYNC_QUEUE, item.id);
    }
};

export const STORE_NAMES = STORES;

export default {
    initOfflineStorage,
    saveVisitOffline,
    saveOrderOffline,
    saveExpenseOffline,
    getPendingSyncItems,
    updateSyncItemStatus,
    getOfflineStats,
    clearSyncedItem,
    STORE_NAMES: STORES
};
